import { useEffect, useState } from 'react';
import { ref as dbRef, onValue, set, remove, onDisconnect } from 'firebase/database';
import { db } from 'utils/firebase-config';

/** Entries older than this are treated as stale (crashed tab / missed clear). */
export const TYPING_TTL_MS = 4000;

/**
 * Clear the sender's typing flag after this long with no keystrokes,
 * even if draft text remains in the input (Telegram/WhatsApp behavior).
 */
export const TYPING_IDLE_MS = 3000;

/**
 * While the user keeps typing, refresh the RTDB node at most this often
 * so peer-side TTL doesn't expire mid-burst. First keystroke is always immediate.
 */
export const TYPING_THROTTLE_MS = 2000;

export type TypingPayload = {
  name: string;
  at: number;
  /** Present when typing in a message thread; null/absent for the main room. */
  threadId?: string | null;
};

export type RoomTyper = {
  pk: string;
  name: string;
  at: number;
};

const typingPath = (roomId: string | number, userPk: string | number) =>
  `typing/${roomId}/${userPk}`;

/**
 * Mark the current user as typing in a room (or a specific thread).
 * Registers onDisconnect cleanup so a closed tab doesn't leave a stuck indicator.
 */
export const setRoomTyping = async (
  roomId: string | number,
  userPk: string | number,
  name: string,
  threadId?: string | null
): Promise<void> => {
  if (roomId == null || userPk == null || !name) return;

  const typingRef = dbRef(db, typingPath(roomId, userPk));
  const payload: TypingPayload = {
    name,
    at: Date.now(),
    threadId: threadId ?? null,
  };

  await set(typingRef, payload);
  try {
    await onDisconnect(typingRef).remove();
  } catch {
    // Auth / offline — write already landed; TTL still covers stuck keys.
  }
};

/** Clear the current user's typing flag for a room. */
export const clearRoomTyping = async (
  roomId: string | number,
  userPk: string | number
): Promise<void> => {
  if (roomId == null || userPk == null) return;

  const typingRef = dbRef(db, typingPath(roomId, userPk));
  try {
    await onDisconnect(typingRef).cancel();
  } catch {
    // Ignore — node may already be gone.
  }
  await remove(typingRef);
};

const parseTypers = (
  val: Record<string, TypingPayload> | null,
  selfPk: string | number | undefined | null,
  matchThread: string | null
): RoomTyper[] => {
  if (!val || typeof val !== 'object') return [];

  const now = Date.now();
  const next: RoomTyper[] = [];

  Object.entries(val).forEach(([pk, entry]) => {
    if (!entry?.name || typeof entry.at !== 'number') return;
    if (String(pk) === String(selfPk)) return;
    if (now - entry.at > TYPING_TTL_MS) return;
    const entryThread = entry.threadId ?? null;
    if (entryThread !== matchThread) return;
    next.push({ pk, name: entry.name, at: entry.at });
  });

  return next;
};

const sameTypers = (a: RoomTyper[], b: RoomTyper[]) =>
  a.length === b.length &&
  a.every((t, i) => t.pk === b[i].pk && t.name === b[i].name && t.at === b[i].at);

/**
 * Live list of *other* users currently typing in a room.
 * Pass `threadId` to scope to a message thread; omit/null for the main feed.
 */
export function useRoomTypers(
  roomId: string | number | undefined | null,
  selfPk: string | number | undefined | null,
  threadId?: string | null
): RoomTyper[] {
  const [typers, setTypers] = useState<RoomTyper[]>([]);

  useEffect(() => {
    if (roomId == null) {
      setTypers([]);
      return undefined;
    }

    const roomTypingRef = dbRef(db, `typing/${roomId}`);
    const matchThread = threadId ?? null;
    // Keep the latest snapshot so the TTL interval can re-filter without another get().
    let latest: Record<string, TypingPayload> | null = null;

    const apply = () => {
      const next = parseTypers(latest, selfPk, matchThread);
      setTypers((prev) => (sameTypers(prev, next) ? prev : next));
    };

    const unsubscribe = onValue(roomTypingRef, (snapshot) => {
      latest = snapshot.val();
      apply();
    });

    // Drop stale entries even when no further RTDB events arrive.
    const tick = setInterval(apply, 1000);

    return () => {
      unsubscribe();
      clearInterval(tick);
      setTypers([]);
    };
  }, [roomId, selfPk, threadId]);

  return typers;
}
